/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable import/no-extraneous-dependencies */

import React, { useState, useEffect } from 'react';
import Model, { helper } from '@tripian/model';
import ReactDOM from 'react-dom';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import emptyImageData from '../../../../constant/emptyImage';
import ImgLazy from '../../../base/ImgLazy/ImgLazy';
import CloseIconButton from '../../../base/Button/Icons/CloseIconButton/CloseIconButton';
import { ChevronLeft, ChevronRight } from '../../../base/Svg/Icons';
import classes from './PoiInfoImageDesktop.scss';

interface IPoiInfoImageDesktop {
  poi: Model.Poi;
  t: (key: Model.TranslationKey) => string;
  onImageModalChange?: (isOpen: boolean) => void;
  stepType?: string;
}

const PoiInfoImageDesktop: React.FC<IPoiInfoImageDesktop> = ({ poi, t, onImageModalChange, stepType }) => {
  const [imageZoomed, setImageZoomed] = useState<{ clicked: boolean; url: string }>({ clicked: false, url: '' });
  const [selectedImage, setSelectedImage] = useState<string>('');

  useEffect(() => {
    if (onImageModalChange) onImageModalChange(imageZoomed.clicked);
  }, [imageZoomed.clicked]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setImageZoomed({ clicked: false, url: '' });
        setSelectedImage('');
      }
    };

    if (imageZoomed.clicked) {
      document.addEventListener('keydown', handleEsc);
    }

    return () => {
      document.removeEventListener('keydown', handleEsc);
    };
  }, [imageZoomed.clicked]);

  const images = poi.gallery.length === 0 ? [{ url: emptyImageData, imageOwner: null }] : poi.gallery.map((img) => ({ url: stepType === 'activity' ? img.url : helper.imgUrl(img.url, 1200), imageOwner: img.imageOwner }));

  const mainImage = images[0];

  if (!mainImage?.url) return null;

  // Görüntülenecek ana resmi seç
  const currentImage = selectedImage || (imageZoomed.clicked ? imageZoomed.url : '');
  const currentIndex = images.findIndex((img) => img.url === currentImage);
  const displayedImage = currentIndex >= 0 ? images[currentIndex] : mainImage;

  // Thumbnail'a tıklandığında
  const handleThumbnailClick = (url: string) => {
    setSelectedImage(url);
  };

  // Önceki resme geç
  const goToPrevImage = () => {
    const index = images.findIndex((img) => img.url === (selectedImage || mainImage.url));
    if (index > 0) {
      const prevImage = images[index - 1];
      setSelectedImage(prevImage.url);
    } else {
      // En sondaki resme git (döngüsel navigasyon)
      const lastImage = images[images.length - 1];
      setSelectedImage(lastImage.url);
    }
  };

  // Sonraki resme geç
  const goToNextImage = () => {
    const index = images.findIndex((img) => img.url === (selectedImage || mainImage.url));
    if (index < images.length - 1) {
      const nextImage = images[index + 1];
      setSelectedImage(nextImage.url);
    } else {
      // İlk resme git (döngüsel navigasyon)
      setSelectedImage(images[0].url);
    }
  };

  const thumbnails = Array.from({ length: 4 }, (_, index) => {
    const image = images[index + 1];

    if (image?.url) {
      return (
        <div
          key={index}
          className={classes.thumbnail}
          onClick={(event) => {
            event.stopPropagation();
            event.preventDefault();
            setImageZoomed({ clicked: true, url: image.url });
          }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setImageZoomed({ clicked: true, url: image.url });
            }
          }}
        >
          <ImgLazy objectFit="cover" src={image.url} alt={`${poi.name} ${index + 2}`} x={400} y={400} />
        </div>
      );
    }
    return <div key={index} className={`${classes.thumbnail} ${classes.placeholder}`} />;
  });

  return (
    <div className={classes.container}>
      {imageZoomed.clicked &&
        ReactDOM.createPortal(
          <div
            className={classes.modalWrapper}
            role="button"
            aria-label="Close image modal"
            tabIndex={0}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setImageZoomed({ clicked: false, url: '' });
                setSelectedImage('');
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape' || e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setImageZoomed({ clicked: false, url: '' });
                setSelectedImage('');
              }
            }}
          >
            <div className={classes.modalContent} role="dialog" aria-modal="true" tabIndex={-1}>
              <div className={classes.modalHeader} style={{ position: 'relative' }}>
                <div className={classes.modalCloseIcon} style={{ position: 'absolute', top: '0.5rem', right: '0.5rem' }}>
                  <CloseIconButton
                    fill="#333"
                    clicked={() => {
                      setImageZoomed({ clicked: false, url: '' });
                      setSelectedImage('');
                    }}
                  />
                </div>
                <h2>{poi.name}</h2>
              </div>

              <div className={classes.modalBody}>
                <div className={classes.thumbnailGallery}>
                  <div className={classes.thumbnailGrid}>
                    {images.map((img, index) => (
                      <div
                        key={`thumb-${img.url}`}
                        className={`${classes.thumbnailItem} ${img.url === displayedImage.url ? classes.active : ''}`}
                        onClick={() => handleThumbnailClick(img.url)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            handleThumbnailClick(img.url);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                      >
                        <ImgLazy objectFit="cover" src={img.url} alt={`${poi.name} ${index + 1}`} x={200} y={200} />
                      </div>
                    ))}
                  </div>
                </div>

                <div className={classes.mainImageContainer} style={{ width: '100%', aspectRatio: '1 / 1', position: 'relative', borderRadius: '0.625rem', overflow: 'hidden' }}>
                  <button type="button" className={`${classes.navButton} ${classes.prev}`} onClick={goToPrevImage} aria-label="Previous image" style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)' }}>
                    <ChevronLeft stroke="#0B1215" />
                  </button>

                  <div style={{ borderRadius: '0.625rem', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
                    <ImgLazy className={classes.mainModalImage} objectFit="cover" src={displayedImage.url} alt={poi.name} x={1200} y={1200} />
                  </div>

                  <button type="button" className={`${classes.navButton} ${classes.next}`} onClick={goToNextImage} aria-label="Next image" style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }}>
                    <ChevronRight stroke="#0B1215" />
                  </button>

                  {displayedImage.imageOwner && displayedImage.imageOwner.url && (
                    <span className={classes.imageOwner}>
                      <a rel="noopener noreferrer" target="_blank" href={`${displayedImage.imageOwner.url}`}>
                        © {t('trips.myTrips.itinerary.step.poi.photo')} {displayedImage.imageOwner?.title}
                      </a>
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>,
          document.getElementById('modal-root') || document.body,
        )}

      <div
        className={classes.mainImage}
        style={{ height: 'auto' }}
        onClick={(event) => {
          event.stopPropagation();
          event.preventDefault();
          setImageZoomed({ clicked: true, url: mainImage.url });
        }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setImageZoomed({ clicked: true, url: mainImage.url });
          }
        }}
      >
        <ImgLazy objectFit="cover" src={mainImage.url} alt={poi.name} x={800} y={800} />
      </div>
      <div className={classes.thumbnailsContainer} style={{ height: 'auto' }}>
        {thumbnails}
      </div>
      <div
        className={classes.showAllPhotos}
        onClick={(event) => {
          event.stopPropagation();
          event.preventDefault();
          setImageZoomed({ clicked: true, url: mainImage.url });
        }}
        role="button"
        tabIndex={0}
        onKeyDown={() => {}}
      >
        {t('trips.myTrips.itinerary.step.poi.showAllPhotos')}
      </div>
    </div>
  );
};

export default PoiInfoImageDesktop;
